import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { X, ChevronLeft } from "lucide-react";
import { useReservationModal } from "../contexts/ReservationModalContext";

/**
 * Two-stage Mr. Black booking embed:
 *   1. Calendar iframe — guests pick an event/date
 *   2. When the calendar postMessages back, swap to the booking-page iframe (full screen)
 *
 * Used until in-house Stripe checkout is wired up. Mirrors Mr Black's official embed snippet.
 */
export default function MrBlackBookingModal() {
  const { isOpen, eventId, close } = useReservationModal();
  const location = useLocation();
  // bookingUrl: when set, we render the full booking iframe instead of the calendar
  const [bookingUrl, setBookingUrl] = useState(null);

  // Close on route change
  useEffect(() => {
    if (isOpen) close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Body scroll lock + Esc + reset state on open/close
  useEffect(() => {
    if (!isOpen) {
      document.body.style.overflow = "";
      setBookingUrl(null);
      return;
    }
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, close]);

  // Listen for Mr Black postMessages
  useEffect(() => {
    if (!isOpen) return;
    const handler = (event) => {
      const data = event.data || {};
      // Mr Black: when user picks an event from the calendar, parent should open the booking iframe
      if (data.action === "openFrame" && data.pageUrl) {
        setBookingUrl(data.pageUrl);
      }
      // Mr Black: when the booking flow signals close, exit the booking iframe
      if (data.type === "close") {
        setBookingUrl(null);
      }
      // Some Mr Black flows post a redirect URL
      if (data.action === "navigate" && data.url) {
        setBookingUrl(data.url);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [isOpen]);

  if (!isOpen) return null;

  const isMobile = typeof window !== "undefined" && window.innerWidth <= 767;
  const calendarBase = isMobile
    ? "https://v2.themrblack.com/calendar/onyx-rooftop-lounge"
    : "https://v2.themrblack.com/calendar-horizontal/onyx-rooftop-lounge";
  const calendarParams = new URLSearchParams({ openFrame: "true" });
  if (eventId) calendarParams.set("eventId", eventId);
  const calendarSrc = `${calendarBase}?${calendarParams.toString()}`;

  return createPortal(
    <div
      data-testid="mrblack-booking-modal"
      className="fixed inset-0 z-[200] bg-[#08080A] flex flex-col"
      style={{ fontFamily: "'TikTok Sans', sans-serif" }}
    >
      {/* Slim header with Onyx branding + back/close */}
      <div className="flex items-center justify-between px-5 md:px-10 py-4 border-b border-white/10 bg-black/80 backdrop-blur-xl flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          {bookingUrl && (
            <button
              onClick={() => setBookingUrl(null)}
              data-testid="mrblack-back"
              aria-label="Back to calendar"
              className="w-9 h-9 border border-white/15 hover:border-white text-white/70 hover:text-white flex items-center justify-center transition flex-shrink-0"
            >
              <ChevronLeft size={16} />
            </button>
          )}
          <div className="min-w-0">
            <p className="text-[10px] tracking-[0.35em] uppercase text-[#D4AF37] font-medium mb-0.5">— Reservation</p>
            <p className="font-display text-lg md:text-xl text-white truncate">
              {bookingUrl ? "Complete your booking" : "Onyx Rooftop · Washington DC"}
            </p>
          </div>
        </div>
        <button
          data-testid="mrblack-close"
          onClick={close}
          aria-label="Close booking"
          className="w-11 h-11 border border-white/15 hover:border-white text-white/70 hover:text-white flex items-center justify-center transition flex-shrink-0"
        >
          <X size={18} />
        </button>
      </div>

      {/* Stage 1: Calendar — always mounted (kept in DOM under the booking iframe so navigation back is instant) */}
      <div className={`flex-1 bg-black overflow-hidden ${bookingUrl ? "hidden" : ""}`}>
        <iframe
          title="Onyx Rooftop Calendar"
          src={calendarSrc}
          allow="payment; clipboard-write"
          className="w-full h-full border-0 block"
        />
      </div>

      {/* Stage 2: Booking page (only when an event has been picked) */}
      {bookingUrl && (
        <div className="flex-1 bg-black overflow-hidden">
          <iframe
            title="Onyx Rooftop Booking"
            src={bookingUrl}
            allow="payment; clipboard-write"
            className="w-full h-full border-0 block"
          />
        </div>
      )}
    </div>,
    document.body
  );
}
